import { ReactNode, useState } from 'react'
import { InputGroup, Textarea, Tooltip } from '@systemeio/components'
import { HelpCircleIcon } from '@systemeio/icons'
import { twJoin } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { AvatarUploader } from 'shared/components/avatar-uploader'
import { FILE_SOURCE } from 'shared/constants'
import {
  UserDomainAccountInterface,
  UserDomainAccountSettingsErrorsInterface,
} from '../types/user-settings-interface'

function HintTooltip({ hint }: { hint: ReactNode }): ReactNode {
  const [open, setOpen] = useState(false)

  return (
    <Tooltip.Root open={open} onOpenChange={setOpen}>
      <Tooltip.Trigger className="relative flex cursor-pointer items-center truncate rounded-full ring-border-accent ring-offset-1 outline-hidden focus-visible:ring-2">
        <HelpCircleIcon
          className={twJoin('size-4', open ? 'fill-icon-accent' : 'fill-text-secondary')}
        />
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner side="top">
          <Tooltip.Popup className="w-[200px] max-w-none px-5 py-3 sm:w-[350px]">
            <div className="w-full text-center font-avertaPE text-sm font-medium break-normal text-text-tertiary">
              {hint}
            </div>
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

interface UserDomainAccountFieldsProps {
  setProfileImage: (dataFile: UserDomainAccountInterface['profileImage']) => void
  setDisplayName: (displayName: UserDomainAccountInterface['displayName']) => void
  setDisplayBio: (bio: UserDomainAccountInterface['bio']) => void
  displayName: UserDomainAccountInterface['displayName']
  bio: UserDomainAccountInterface['bio']
  profileImage: string
  email: string
  errors: UserDomainAccountSettingsErrorsInterface
}

function UserDomainAccountFields({
  displayName,
  bio,
  setDisplayName,
  setDisplayBio,
  setProfileImage,
  profileImage,
  email,
  errors,
}: UserDomainAccountFieldsProps): ReactNode {
  const { t } = useTranslation()

  return (
    <>
      <div className="flex flex-col justify-between gap-0 *:flex-1 md:flex-row md:gap-10">
        <div className={'flex basis-full flex-col items-start gap-1'}>
          <div className="flex gap-1.5 font-avertaPE font-normal text-text-secondary">
            {t('user.profile_settings.profile_image')}
            <HintTooltip hint={t('user.profile_settings.profile_image_hint')} />
          </div>
          <AvatarUploader
            type="profile_image"
            source={FILE_SOURCE.settings}
            onSetPhoto={setProfileImage}
          >
            <img
              className={`rounded-full border-2 border-border-accent object-cover`}
              alt={email}
              width={128}
              height={128}
              src={profileImage}
            />
          </AvatarUploader>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-1" data-testid="display-name-field">
        <label className="flex items-center gap-1 text-sm font-medium text-text-primary">
          <span className="flex min-w-0 items-center gap-1.5 font-avertaPE text-base font-normal text-text-secondary">
            {t('user.profile_settings.display_name.label')}
            <HintTooltip hint={t('user.profile_settings.display_name.help_message')} />
          </span>
          <span className="shrink-0 text-text-error">*</span>
        </label>
        <InputGroup.Root data-invalid={!!errors.displayName || undefined}>
          <InputGroup.Input
            data-testid="display-name-input"
            placeholder={t('user.profile_settings.first_name.label')}
            required
            value={displayName || ''}
            onChange={e => setDisplayName(e.target.value)}
          />
        </InputGroup.Root>
        {errors.displayName && <p className="text-sm text-text-error">{errors.displayName}</p>}
      </div>
      <div className="flex flex-col justify-between gap-0 *:flex-1 md:flex-row md:gap-10">
        <div className="flex min-w-0 flex-1 flex-col gap-1" data-testid="bio-field">
          <label className="text-sm font-medium text-text-primary">
            <div className="flex gap-1.5 font-avertaPE text-base font-normal text-text-secondary">
              {t('user.profile_settings.display_bio.label')}
              <HintTooltip hint={t('user.profile_settings.display_bio.help_message')} />
            </div>
          </label>
          <Textarea
            autoresize
            rows={5}
            placeholder={t('user.profile_settings.display_bio.input_placeholder')}
            maxLength={255}
            value={bio || ''}
            onChange={e => setDisplayBio(e.target.value)}
          />
          {errors.bio ? <p className="text-sm text-text-error">{errors.bio}</p> : null}
        </div>
        <div />
      </div>
    </>
  )
}

export default UserDomainAccountFields
